import {
  Sidebar,
  type SidebarProps,
} from "@sylviq/ui-react-web/components/shell/Sidebar";
import {
  Nav,
  NavItem,
  NavSection,
} from "@sylviq/ui-react-web/components/shell/SidebarNav";
import type { ComponentType, ReactNode } from "react";

export type AppNavigationIcon = ComponentType<{ className?: string }>;

/** A view inside a navigation section. `destination` is opaque to this block;
 * the app's renderer turns it back into a router link. */
export interface AppNavigationItem<Meta = unknown> {
  id: string;
  label: string;
  icon?: AppNavigationIcon;
  active?: boolean;
  trailing?: ReactNode;
  /** Kept in the model but omitted from the rendered navigation. */
  hidden?: boolean;
  /** Marks a child destination as secondary, for app-specific rendering. */
  secondary?: boolean;
  destination?: Meta;
}

/** A top-level navigation section. With zero or one visible child it renders
 * as that section's own destination; with two or more it opens a flyout. */
export interface AppNavigationSection<Meta = unknown> {
  id: string;
  label: string;
  icon?: AppNavigationIcon;
  /** The section holds the current page. Never inferred from the path. */
  active?: boolean;
  trailing?: ReactNode;
  hidden?: boolean;
  /** Opaque payload for the section's own destination. */
  destination?: Meta;
  children?: readonly AppNavigationItem<Meta>[];
}

/** Normalized destination handed to the app's renderer. */
export interface AppNavigationDestination<Meta = unknown> {
  id: string;
  label: string;
  icon?: AppNavigationIcon;
  active: boolean;
  trailing?: ReactNode;
  kind: "primary" | "child";
  sectionId: string;
  secondary: boolean;
  meta: Meta | undefined;
}

export type AppNavigationDestinationRenderer<Meta = unknown> = (
  destination: AppNavigationDestination<Meta>,
) => ReactNode;

export interface AppNavigationProps<Meta = unknown> {
  /** Accessible name for the navigation landmark. */
  label: string;
  sections: readonly AppNavigationSection<Meta>[];
  /** Render the router-specific link for a destination. Must return a single
   * element; the shared `NavItem` composes the icon, label, trailing content,
   * and active state into it. */
  renderDestination: AppNavigationDestinationRenderer<Meta>;
  footer?: ReactNode;
  aside?: ReactNode;
  /** Products own their brand; pass a lockup slot when the app shows one. */
  lockup?: ReactNode;
  sidebarLabel?: string;
  className?: string;
  expandLabel?: string;
  collapseLabel?: string;
  /** Extra props for the underlying `Sidebar`, such as `style` or data
   * attributes. Slot and label props are owned by this block. */
  sidebarProps?: Omit<
    SidebarProps,
    | "lockup"
    | "aside"
    | "footer"
    | "children"
    | "className"
    | "aria-label"
    | "expandLabel"
    | "collapseLabel"
  >;
}

function primaryDestination<Meta>(
  section: AppNavigationSection<Meta>,
): AppNavigationDestination<Meta> {
  return {
    id: section.id,
    label: section.label,
    icon: section.icon,
    active: section.active ?? false,
    trailing: section.trailing,
    kind: "primary",
    sectionId: section.id,
    secondary: false,
    meta: section.destination,
  };
}

function childDestination<Meta>(
  section: AppNavigationSection<Meta>,
  item: AppNavigationItem<Meta>,
): AppNavigationDestination<Meta> {
  return {
    id: item.id,
    label: item.label,
    icon: item.icon,
    active: item.active ?? false,
    trailing: item.trailing,
    kind: "child",
    sectionId: section.id,
    secondary: item.secondary ?? false,
    meta: item.destination,
  };
}

/**
 * Router-agnostic application navigation. It owns the standard sidebar
 * structure — sections, direct destinations, flyouts, active state — while the
 * app owns route links and destination search through `renderDestination`.
 */
export function AppNavigation<Meta = unknown>({
  label,
  sections,
  renderDestination,
  footer,
  aside,
  lockup,
  sidebarLabel,
  className,
  expandLabel,
  collapseLabel,
  sidebarProps,
}: AppNavigationProps<Meta>) {
  return (
    <Sidebar
      {...sidebarProps}
      lockup={lockup}
      aside={aside}
      footer={footer}
      aria-label={sidebarLabel}
      className={className}
      expandLabel={expandLabel}
      collapseLabel={collapseLabel}
    >
      <Nav label={label}>
        {sections
          .filter((section) => !section.hidden)
          .map((section) => {
            const children = (section.children ?? []).filter(
              (item) => !item.hidden,
            );

            if (children.length < 2) {
              const destination = primaryDestination(section);
              return (
                <NavItem
                  key={section.id}
                  asChild
                  icon={destination.icon}
                  label={destination.label}
                  active={destination.active}
                  trailing={destination.trailing}
                >
                  {renderDestination(destination)}
                </NavItem>
              );
            }

            return (
              <NavSection
                key={section.id}
                id={section.id}
                icon={section.icon}
                label={section.label}
                current={section.active}
                trailing={section.trailing}
              >
                {children.map((item) => {
                  const destination = childDestination(section, item);
                  return (
                    <NavItem
                      key={item.id}
                      asChild
                      icon={destination.icon}
                      label={destination.label}
                      active={destination.active}
                      trailing={destination.trailing}
                    >
                      {renderDestination(destination)}
                    </NavItem>
                  );
                })}
              </NavSection>
            );
          })}
      </Nav>
    </Sidebar>
  );
}
